/**
 * usePagination Hook
 * 
 * Konsoliderar 10+ paginerings-implementationer i kodbasen.
 * Hanterar page, pageSize, total, och navigation.
 * 
 * Användning:
 * const pagination = usePagination({ initialPageSize: 25 });
 * const { data } = useAsyncData('/api/users', { params: { page: pagination.page, limit: pagination.pageSize } });
 * pagination.setTotal(response.total);
 */

import { useState, useCallback, useMemo } from 'react';

export interface UsePaginationOptions {
  /** Initial page number (1-based) */
  initialPage?: number;
  /** Initial page size */
  initialPageSize?: number;
  /** Available page size options */
  pageSizeOptions?: number[];
  /** Total number of items (can be set later) */
  initialTotal?: number;
  /** Callback when page changes */
  onPageChange?: (page: number) => void;
  /** Callback when page size changes */
  onPageSizeChange?: (pageSize: number) => void;
}

export interface UsePaginationResult {
  /** Current page (1-based) */
  page: number;
  /** Current page size */
  pageSize: number;
  /** Total number of items */
  total: number;
  /** Total number of pages */
  totalPages: number;
  /** Available page size options */
  pageSizeOptions: number[];
  /** Whether there is a previous page */
  hasPrevious: boolean;
  /** Whether there is a next page */
  hasNext: boolean;
  /** Start index for current page (0-based) */
  startIndex: number;
  /** End index for current page (0-based, exclusive) */
  endIndex: number;
  /** Offset for SQL queries */
  offset: number;
  /** Set current page */
  setPage: (page: number) => void;
  /** Set page size (resets to page 1) */
  setPageSize: (size: number) => void;
  /** Set total count */
  setTotal: (total: number) => void;
  /** Go to next page */
  nextPage: () => void;
  /** Go to previous page */
  previousPage: () => void;
  /** Go to first page */
  firstPage: () => void;
  /** Go to last page */
  lastPage: () => void;
  /** Reset pagination to initial state */
  reset: () => void;
  /** Get page numbers for pagination display */
  getPageNumbers: (maxVisible?: number) => (number | 'ellipsis')[];
}

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export function usePagination(options: UsePaginationOptions = {}): UsePaginationResult {
  const {
    initialPage = 1,
    initialPageSize = 25,
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
    initialTotal = 0,
    onPageChange,
    onPageSizeChange,
  } = options;

  const [page, setPageState] = useState(initialPage);
  const [pageSize, setPageSizeState] = useState(initialPageSize);
  const [total, setTotal] = useState(initialTotal);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);
  const hasPrevious = page > 1;
  const hasNext = page < totalPages;
  const startIndex = (page - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, total);
  const offset = startIndex;

  const setPage = useCallback((newPage: number) => {
    const validPage = Math.max(1, Math.min(newPage, totalPages));
    setPageState(validPage);
    onPageChange?.(validPage);
  }, [totalPages, onPageChange]);

  const setPageSize = useCallback((newSize: number) => {
    setPageSizeState(newSize);
    setPageState(1); // Reset to first page
    onPageSizeChange?.(newSize);
  }, [onPageSizeChange]);

  const nextPage = useCallback(() => {
    if (hasNext) setPage(page + 1);
  }, [hasNext, page, setPage]);

  const previousPage = useCallback(() => {
    if (hasPrevious) setPage(page - 1);
  }, [hasPrevious, page, setPage]);

  const firstPage = useCallback(() => setPage(1), [setPage]);
  const lastPage = useCallback(() => setPage(totalPages), [setPage, totalPages]);

  const reset = useCallback(() => {
    setPageState(initialPage);
    setPageSizeState(initialPageSize);
    setTotal(initialTotal);
  }, [initialPage, initialPageSize, initialTotal]);

  const getPageNumbers = useCallback((maxVisible = 7): (number | 'ellipsis')[] => {
    if (totalPages <= maxVisible) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages: (number | 'ellipsis')[] = [];
    const sidePages = Math.floor((maxVisible - 3) / 2);

    // Always show first page
    pages.push(1);

    if (page > sidePages + 2) {
      pages.push('ellipsis');
    }

    // Pages around current page
    const start = Math.max(2, page - sidePages);
    const end = Math.min(totalPages - 1, page + sidePages);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (page < totalPages - sidePages - 1) {
      pages.push('ellipsis');
    }

    // Always show last page
    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  }, [page, totalPages]);

  return {
    page,
    pageSize,
    total,
    totalPages,
    pageSizeOptions,
    hasPrevious,
    hasNext,
    startIndex,
    endIndex,
    offset,
    setPage,
    setPageSize,
    setTotal,
    nextPage,
    previousPage,
    firstPage,
    lastPage,
    reset,
    getPageNumbers,
  };
}

export default usePagination;
